"use client";

import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { format, parseISO } from "date-fns";
import {
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ClockIcon,
  CurrencyDollarIcon,
  MapPinIcon,
} from "@heroicons/react/20/solid";
import {
  MegaphoneIcon,
  TicketIcon,
  PauseIcon,
} from "@heroicons/react/24/solid";
import {
  TicketIcon as TicketIconOutline,
  CurrencyDollarIcon as CurrencyDollarIconOutline,
} from "@heroicons/react/24/outline";
import { EventData } from "@types";
import { useWindowSize } from "@utils/hooks";
import { joinClassNames } from "@utils/helpers";
import EventToolTip from "./eventToolTip";
import Outlet from "./outlet";
import { useIsShopsite } from "@context/DomainContext";
import { Button, Card, Flex, Heading, Text } from "@components/theming";
import EventTicketButton from "./eventTicketButton";

interface Props {
  event: EventData;
  priceRangeText?: string;
}

export default function EventInfo({ event, priceRangeText }: Props) {
  const portraitImage = event.images
    .filter((i) => i.format.name.includes("Portrait"))
    .sort((a, b) => {
      if (a.format.width > b.format.width) {
        return -1;
      }
      if (a.format.width < b.format.width) {
        return 1;
      }
      return 0;
    })[0];
  const windowSize = useWindowSize();
  const searchParams = useSearchParams();
  const viewMode = searchParams.get("view");
  const promo = searchParams.get("promo");
  const t = useTranslations("EventPage");
  const isShopsite = useIsShopsite();

  const showEventDescription = viewMode?.includes("info");

  return isShopsite &&
    windowSize.width &&
    windowSize.width < 1024 &&
    !showEventDescription &&
    event.status !== "past" ? (
    <></>
  ) : (
    <>
      {portraitImage && isShopsite ? (
        <Image
          className="rounded-xl hidden lg:block"
          src={`${portraitImage.path.processed.trim()}?v=${
            portraitImage.version
          }`}
          width={264}
          height={308}
          alt="Event Image"
          priority
          quality={100}
        />
      ) : isShopsite ? (
        <div className="hidden lg:flex items-center justify-center rounded-xl aspect-[6/7] max-w-[264px] bg-indigo-100">
          <MegaphoneIcon className="text-indigo-800 w-1/2 h-1/2" />
        </div>
      ) : (
        <></>
      )}
      <Card size="3" shopsiteClasses="flex flex-col gap-4 mt-5">
        <Flex
          direction="column"
          gap="1"
          shopsiteClasses="bg-gray-50 rounded-2xl px-4 py-2"
        >
          <div className="flex flex-row items-center gap-2 flex-wrap">
            <div className="flex gap-1 items-center">
              {isShopsite && (
                <MapPinIcon className="h-5 w-5 text-black" aria-hidden="true" />
              )}
              <Heading
                as="h2"
                weight="bold"
                shopsiteClasses="text-base font-semibold text-black"
              >
                {t("where")}
              </Heading>
            </div>
            {event.venue.slug ? (
              <Button
                href={event.venue.slug}
                variant="soft"
                shopsiteVariant="soft"
                shopsiteClasses="gap-1 py-1 !rounded-lg"
              >
                <BuildingOfficeIcon className="h-5 w-5" />
                <span className="font-medium">{t("viewvenue")}</span>
              </Button>
            ) : (
              <></>
            )}
          </div>
          <div>
            <Text as="p" color="gray" shopsiteClasses="text-gray-500">
              {event.venue.name}
            </Text>
            <Text as="p" color="gray" shopsiteClasses="text-gray-500">
              {event.venue.addressline1}
            </Text>
            <Text as="p" color="gray" shopsiteClasses="text-gray-500">
              {`${event.venue.location}, ${event.venue.country}`}
            </Text>
          </div>
        </Flex>
        <Flex
          direction="column"
          gap="1"
          mt="4"
          shopsiteClasses="bg-gray-50 rounded-2xl px-4 py-2"
        >
          <div className="flex gap-1 items-center">
            {isShopsite && (
              <CalendarDaysIcon
                className="h-5 w-5 text-black"
                aria-hidden="true"
              />
            )}
            <Heading
              as="h2"
              weight="bold"
              shopsiteClasses="text-base font-semibold text-black"
            >
              {t("when")}
            </Heading>
          </div>
          <Text as="p" color="gray" shopsiteClasses="text-gray-500">
            {event.info.daterange
              ? event.info.daterange
              : `${format(
                  parseISO(event.showings[0].date.local.start),
                  `EEE d MMM yyyy`
                )}${
                  event.showings.length > 1 &&
                  format(
                    parseISO(event.showings[0].date.local.start),
                    `EEE d MMM yyyy`
                  ) !==
                    format(
                      parseISO(
                        event.showings[event.showings.length - 1].date.local
                          .start
                      ),
                      `EEE d MMM yyyy`
                    )
                    ? ` - ${format(
                        parseISO(
                          event.showings[event.showings.length - 1].date.local
                            .start
                        ),
                        `EEE d MMM yyyy`
                      )}`
                    : ""
                }`}
          </Text>
        </Flex>
        {event.info.runningtime !== "" ? (
          <Flex
            direction="column"
            gap="1"
            mt="4"
            shopsiteClasses="bg-gray-50 rounded-2xl px-4 py-2"
          >
            <div className="flex gap-1 items-center">
              {isShopsite && (
                <ClockIcon className="h-5 w-5 text-black" aria-hidden="true" />
              )}
              <Heading
                as="h2"
                weight="bold"
                shopsiteClasses="text-base font-semibold text-black"
              >
                {t("runningtime")}
              </Heading>
            </div>
            <Text as="p" color="gray" shopsiteClasses="text-gray-500">
              {event.info.runningtime}
            </Text>
          </Flex>
        ) : (
          <></>
        )}
        {event.info.intervaltime !== "" ? (
          <Flex
            direction="column"
            gap="1"
            mt="4"
            shopsiteClasses="bg-gray-50 rounded-2xl px-4 py-2"
          >
            <div className="flex gap-1 items-center">
              {isShopsite && (
                <span className="relative">
                  <ClockIcon
                    className="h-5 w-5 text-black"
                    aria-hidden="true"
                  />
                  <PauseIcon className="absolute rounded-sm -bottom-px -right-px h-2.5 w-2.5 text-black bg-gray-50" />
                </span>
              )}
              <Heading
                as="h2"
                weight="bold"
                shopsiteClasses="text-base font-semibold text-black"
              >
                {t("interval")}
              </Heading>
            </div>
            <Text as="p" color="gray" shopsiteClasses="text-gray-500">
              {event.info.intervaltime}
            </Text>
          </Flex>
        ) : (
          <></>
        )}
        {!isShopsite && priceRangeText ? (
          <Flex direction="column" gap="1" mt="4">
            <Heading as="h2" weight="bold">
              {t("pricerange")}
            </Heading>
            <div>
              <Text as="p" color="gray">
                {event.info.pricerange ? event.info.pricerange : priceRangeText}
              </Text>
            </div>
          </Flex>
        ) : (
          <></>
        )}
        {event.delivery.filter(
          (delivery) =>
            delivery.type.public &&
            !delivery.type.free &&
            !delivery.name.toLowerCase().includes("real time")
        ).length > 0 && (
          <Flex
            direction="column"
            gap="1"
            mt="4"
            shopsiteClasses="bg-gray-50 rounded-2xl px-4 py-2"
          >
            <div className="flex gap-1 items-center">
              {isShopsite && (
                <TicketIcon className="h-5 w-5 text-black" aria-hidden="true" />
              )}
              <Heading
                as="h2"
                weight="bold"
                shopsiteClasses="text-base font-semibold text-black"
              >
                {t("deliveryoptions")}
              </Heading>
              {event.delivery.filter(
                (delivery) =>
                  delivery.type.public &&
                  !delivery.type.free &&
                  !delivery.name.toLowerCase().includes("real time")
              ).length > 1 ? (
                <EventToolTip
                  title={t("deliverytooltip.heading")}
                  description={t("deliverytooltip.description")}
                  icon={
                    <TicketIconOutline
                      className={joinClassNames(
                        "absolute top-0 left-4 w-24 h-24 -rotate-12",
                        isShopsite
                          ? "text-white/10"
                          : "text-[color:var(--accent-a3)]"
                      )}
                    />
                  }
                />
              ) : null}
            </div>
            <div>
              {event.delivery
                .filter(
                  (delivery) =>
                    delivery.type.public &&
                    !delivery.type.free &&
                    !delivery.name.toLowerCase().includes("real time")
                )
                .map((delivery) => (
                  <Text
                    key={delivery.id}
                    as="p"
                    color="gray"
                    shopsiteClasses="text-gray-500"
                  >
                    {delivery.name}
                  </Text>
                ))}
            </div>
          </Flex>
        )}
        <Flex
          direction="column"
          gap="1"
          mt="4"
          shopsiteClasses="bg-gray-50 rounded-2xl px-4 py-2"
        >
          <div className="flex gap-1 items-center">
            {isShopsite && (
              <CurrencyDollarIcon
                className="h-5 w-5 text-black"
                aria-hidden="true"
              />
            )}
            <Heading
              as="h2"
              weight="bold"
              shopsiteClasses="text-base font-semibold text-black"
            >
              {t("paymentoptions")}
            </Heading>
            {event.payment.filter(
              (payment) => payment.type.public && payment.type.website
            ).length > 1 ? (
              <EventToolTip
                title={t("paymenttooltip.heading")}
                description={t("paymenttooltip.description")}
                icon={
                  <CurrencyDollarIconOutline
                    className={joinClassNames(
                      "absolute top-0 left-4 w-24 h-24 -rotate-12",
                      isShopsite
                        ? "text-white/10"
                        : "text-[color:var(--accent-a3)]"
                    )}
                  />
                }
              />
            ) : null}
          </div>
          <div>
            {event.payment
              .filter((payment) => payment.type.public && payment.type.website)
              .map((payment) => (
                <div
                  key={payment.id}
                  className="flex justify-between items-center"
                >
                  <Text as="p" color="gray" shopsiteClasses="text-gray-500">
                    {payment.name}
                  </Text>
                </div>
              ))}
          </div>
        </Flex>
        {event.outlets.length > 0 ? <Outlet outlets={event.outlets} /> : <></>}
        {!isShopsite && event.status !== "past" ? (
          <div className="max-md:hidden">
            <EventTicketButton
              showEventDescription={showEventDescription}
              href={
                showEventDescription
                  ? `${event.slug}${promo ? `?promo=${promo}` : ""}`
                  : `${event.slug}?${promo ? `promo=${promo}&` : ""}view=info`
              }
            />
          </div>
        ) : (
          <></>
        )}
      </Card>
    </>
  );
}
